import { useCallback } from 'react'

import {
  buildPermissionSubmissionPayloads,
  isPermissionPayloadEmpty,
} from 'features/recaps/modals/share_modal/utils'
import {
  useDeleteRecapPermissionsMutation,
  usePostRecapPermissionsMutation,
} from 'hooks/notetaker/api'

import { emptyPendingRecipients } from './constants'

const removeKeys = (map, keys) => {
  if (keys.length === 0) {
    return map
  }

  const next = { ...map }
  keys.forEach((key) => {
    delete next[key]
  })

  return next
}

const partitionPendingKeys = (pendingMap) => {
  const granted = []
  const removed = []

  Object.entries(pendingMap ?? {}).forEach(([uuid, relation]) => {
    if (relation === null) {
      removed.push(uuid)
    } else {
      granted.push(uuid)
    }
  })

  return { granted, removed }
}

export const useSubmitPermissions = ({
  trackedMeetingUuid,
  pendingPermissionUpdates,
  pendingRecipients,
  accessList,
  setPendingPermissionUpdates,
  setPendingRecipients,
  applyPostMutationResult,
  applyDeleteMutationResult,
}) => {
  const [postRecapPermissions, { isLoading: isPosting }] = usePostRecapPermissionsMutation()
  const [deleteRecapPermissions, { isLoading: isDeleting }] = useDeleteRecapPermissionsMutation()

  const submit = useCallback(async () => {
    if (!trackedMeetingUuid) {
      return
    }

    const recipientsAtSubmit = pendingRecipients

    const { postPayload, deletePayload } = buildPermissionSubmissionPayloads({
      pendingPermissionUpdates,
      pendingRecipients: recipientsAtSubmit,
      accessList,
    })

    const userKeys = partitionPendingKeys(pendingPermissionUpdates.users)
    const groupKeys = partitionPendingKeys(pendingPermissionUpdates.userGroups)
    const orgRelation = pendingPermissionUpdates.organization

    const tasks = []

    if (!isPermissionPayloadEmpty(postPayload)) {
      tasks.push(
        postRecapPermissions({ meetingUuid: trackedMeetingUuid, ...postPayload })
          .unwrap()
          .then((postResponse) => {
            applyPostMutationResult(postResponse, recipientsAtSubmit)
            setPendingPermissionUpdates((current) => ({
              ...current,
              users: removeKeys(current.users, userKeys.granted),
              userGroups: removeKeys(current.userGroups, groupKeys.granted),
              organization: orgRelation != null ? undefined : current.organization,
            }))
            setPendingRecipients(emptyPendingRecipients)
          }),
      )
    }

    if (!isPermissionPayloadEmpty(deletePayload)) {
      tasks.push(
        deleteRecapPermissions({ meetingUuid: trackedMeetingUuid, ...deletePayload })
          .unwrap()
          .then(() => {
            applyDeleteMutationResult(deletePayload)
            setPendingPermissionUpdates((current) => ({
              ...current,
              users: removeKeys(current.users, userKeys.removed),
              userGroups: removeKeys(current.userGroups, groupKeys.removed),
              organization: orgRelation === null ? undefined : current.organization,
            }))
          }),
      )
    }

    const results = await Promise.allSettled(tasks)
    const failedResult = results.find((result) => result.status === 'rejected')

    if (failedResult) {
      throw failedResult.reason
    }
  }, [
    accessList,
    applyDeleteMutationResult,
    applyPostMutationResult,
    deleteRecapPermissions,
    pendingPermissionUpdates,
    pendingRecipients,
    postRecapPermissions,
    setPendingPermissionUpdates,
    setPendingRecipients,
    trackedMeetingUuid,
  ])

  return {
    submit,
    isSubmitting: isPosting || isDeleting,
  }
}
